// Copied verbatim from original/src/components/CustomSelect.tsx (ADR-08 §2.1); the three inline Arabic strings go through t() (D-055).
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface SelectOption {
  id: string | number;
  label: string;
  subLabel?: string;
  badge?: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (value: any) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  headerTitle?: string;
  label?: string;
  searchable?: boolean;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  align?: 'right' | 'left';
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  headerTitle,
  label,
  searchable = true,
  className = '',
  triggerClassName = '',
  dropdownClassName = '',
  disabled = false,
  size = 'md',
  icon,
  align = 'right',
}) => {
  const t = useT();
  const resolvedPlaceholder = placeholder ?? t('select.placeholder');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => String(o.id) === String(value));

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredOptions = options.filter((o) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.label.toLowerCase().includes(q) ||
      (o.subLabel && o.subLabel.toLowerCase().includes(q)) ||
      (o.badge && o.badge.toLowerCase().includes(q))
    );
  });

  const sizeClasses = {
    sm: 'px-3 py-2 text-xs rounded-xl',
    md: 'px-3.5 py-2.5 text-xs font-bold rounded-2xl',
    lg: 'px-4 py-3 text-sm font-bold rounded-2xl',
  };

  const defaultSearchPlaceholder = searchPlaceholder || (label ? t('select.search_or', { label }) : t('select.search'));

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between bg-white dark:bg-[#161b22] border transition-all cursor-pointer shadow-sm text-right ${
          sizeClasses[size]
        } ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-[#30363d]'
            : isOpen
            ? 'border-blue-500 ring-2 ring-blue-500/20 bg-white dark:bg-[#161b22]'
            : 'border-gray-200 dark:border-[#30363d] hover:border-gray-300 dark:hover:border-gray-600'
        } ${triggerClassName}`}
      >
        <div className="flex items-center gap-2 truncate flex-1 min-w-0">
          {icon && <span className="text-gray-400 dark:text-gray-500 shrink-0">{icon}</span>}
          {selectedOption ? (
            <div className="flex items-center justify-between w-full gap-2 min-w-0">
              <span className="font-bold text-gray-900 dark:text-white truncate flex-1 min-w-0">
                {selectedOption.label}
              </span>
              {selectedOption.subLabel && (
                <span className="text-[11px] font-mono font-bold text-emerald-600 dark:text-emerald-400 dir-ltr text-left shrink-0">
                  {selectedOption.subLabel}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 font-normal truncate">
              {resolvedPlaceholder}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 dark:text-gray-500 shrink-0 mr-2 transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-blue-500' : ''
          }`}
        />
      </button>

      {/* Options Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute top-full ${align === 'left' ? 'left-0' : 'right-0'} mt-1.5 z-50 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-2xl shadow-2xl overflow-hidden max-h-80 flex flex-col animate-in fade-in zoom-in-95 duration-150 min-w-full sm:min-w-[280px] max-w-[calc(100vw-2rem)] ${dropdownClassName}`}
        >
          {/* Search Box */}
          {searchable && (
            <div className="p-2.5 border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#0d1117]/80">
              <div className="relative flex items-center">
                <Search size={14} className="absolute right-3 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={defaultSearchPlaceholder}
                  className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl pr-9 pl-3 py-2 text-xs font-bold text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Header Title (as shown in screenshot: قائمة البطاقات المصرفية والمحافظ الفرعية) */}
          {headerTitle && (
            <div className="px-3.5 py-2 text-[11px] font-bold text-gray-400 dark:text-gray-500 border-b border-gray-100/80 dark:border-[#30363d]/60 bg-gray-50/40 dark:bg-[#161b22]/40">
              {headerTitle}
            </div>
          )}

          {/* List Options */}
          <div className="overflow-y-auto divide-y divide-gray-100 dark:divide-[#30363d]/40 max-h-60 scrollbar-thin">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400 font-bold">
                {t('select.no_results')}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.id) === String(value);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      onChange(opt.id);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-bold transition-all text-right cursor-pointer group ${
                      isSelected
                        ? 'bg-blue-50/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-black'
                        : 'hover:bg-gray-50 dark:hover:bg-[#21262d] text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate flex-1 min-w-0">
                      {isSelected ? (
                        <Check size={14} className="text-blue-600 dark:text-blue-400 shrink-0" />
                      ) : opt.icon ? (
                        <span className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 shrink-0">
                          {opt.icon}
                        </span>
                      ) : null}
                      <span className="truncate">{opt.label}</span>
                      {opt.badge && (
                        <span className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] px-2 py-0.5 rounded-full font-bold shrink-0">
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    {opt.subLabel && (
                      <span
                        dir="ltr"
                        className={`font-mono text-left font-bold text-[11px] shrink-0 mr-2 ${
                          isSelected
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {opt.subLabel}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
